import { useState, useEffect } from 'react';
import { Box, Flex, Text, Skeleton, Icon, IconButton } from '@chakra-ui/react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    LineChart, Line, Cell, LabelList, ReferenceLine,
} from 'recharts';
import { TbTrendingUp, TbTrendingDown, TbArrowUp, TbArrowDown } from 'react-icons/tb';
import {
    InvoicingTimeseriesResponse,
    InvoicingTimeseriesDataPoint,
    InvoicingLocationSegment,
    InvoicingVendorSegment,
    InvoiceTypeKey,
    FlagTypeKey,
    ReportingComparison,
    ComparisonChange,
} from '../../../types/reporting';
import { getTimeseriesComparisonLabel } from '../shared/reportingComparisonLabels';
import type { ChartStyle, WidgetDomain, WidgetSort } from '../widgets/types';
import { invoicingMetricAllowsDonut, INVOICING_DEFAULT_LOCATION_LINE_CUTOFF } from '../widgets/invoicingCatalog';
import {
    BAR_SERIES_COLORS,
    PIE_DONUT_COLORS,
    PRIMARY_CHART_COLOR,
    PRIOR_COMPARISON_BAR_FILL,
    PRIOR_COMPARISON_BAR_FILL_OPACITY,
    formatChartValue,
} from '../shared/formatters';
import {
    formatPeriodAxisLabel,
    formatPeriodTooltipLabel,
    getPartialPeriodLabel,
    getRankingXAxisLayout,
    getTimeseriesPlotWidth,
    getTimeseriesXAxisLayout,
    isPartialReportingPeriod,
    getRankingScrollbarGutterPx,
    needsRankingScrollbarGutter,
    RANKING_AXIS_TICK_MARGIN,
    resolveComparablePriorPeriodLabel,
    truncateRankingAxisName,
} from '../shared/dateUtils';
import { timeseriesXAxisTickRenderer } from '../shared/TimeseriesDateTick';
import { MARKETPLACE_CHART_TOP_MARGIN, MarketplaceChartLegend } from '../shared/MarketplaceChartLegend';
import { foldDonutOther, renderDonutValueLabel, ReportingDonut } from '../shared/ReportingDonut';
import { useChartContainerWidth } from '../shared/useChartContainerWidth';
import { useReportingChartFullscreen } from '../shared/reportingChartFullscreen';
import { ComparisonTooltipFooter } from '../shared/ComparisonTooltipFooter';
import { resolveComparablePriorMetricValue } from '../shared/reportingPriorValues';
import {
    filterTimeseriesPointsForDisplay,
    getInvoicingTimeseriesActivity,
} from '../shared/reportingTimeseriesVisibility';
import { getBarChartYDomain, getBarChartYTicks } from '../../../utils/chartUtils';
import { calculateMetricChange } from '../shared/metricChange';
import {
    BAR_VALUE_FONT,
    chartValueFontSize,
    lineChartPlotLeftPad,
    linePointLabelListContent,
    renderVerticalBarValueLabel,
    stackedSegmentLabelListContent,
    VERTICAL_BAR_LABEL_MIN_WIDTH_COMPARISON,
    VERTICAL_BAR_LABEL_MIN_WIDTH_SINGLE,
    yAxisTickFontSize,
    yAxisWidth,
} from '../shared/barValueLabels';

const chartSeriesColor = (index: number): string => BAR_SERIES_COLORS[index % BAR_SERIES_COLORS.length];

const INVOICE_TYPE_COLOR: Record<InvoiceTypeKey, string> = {
    received: BAR_SERIES_COLORS[0],
    returned: BAR_SERIES_COLORS[1],
    return_slip: BAR_SERIES_COLORS[2],
    sublet: BAR_SERIES_COLORS[3],
};

const NEGATIVE_METRICS = new Set([
    'invoiceFlags',
    'invoiceFlagsValue',
    'totalCostVariance',
    'avgCostVariance',
    'avgDeliveryVarianceDays',
]);

interface InvoicingTimeseriesChartProps {
    data: InvoicingTimeseriesResponse | undefined;
    isLoading: boolean;
    metric: string;
    domain: WidgetDomain;
    chartStyle: ChartStyle;
    height?: number | string;
    comparison?: ReportingComparison;
    preset?: string;
    granularity?: string;
    /** Inclusive selected range start — clips first partial week/month tooltip dates. */
    periodStartDate?: string;
    /** Inclusive selected range end — clips partial week/biweekly axis labels. */
    periodEndDate?: string;
    showComparisonBars?: boolean;
    initialSortOrder?: WidgetSort;
    onSortOrderChange?: (sort: WidgetSort) => void;
    locationCutoff?: number;
    locationShowAll?: boolean;
    /** Selected invoice types. Empty or omitted keeps every type on breakdown charts. */
    invoiceTypes?: InvoiceTypeKey[];
    /** Opens the invoice feed for a bar, stacked section, or doughnut slice. */
    onSegmentClick?: (segment: { seriesKey: string; seriesLabel?: string; bucketStart?: string }) => void;
}

type Unit = 'count' | 'value' | 'percent';
type CategoryDim = 'none' | 'invoiceType' | 'flagType' | 'location' | 'vendor';

const INVOICE_TYPE_ORDER: InvoiceTypeKey[] = ['received', 'returned', 'return_slip', 'sublet'];
const INVOICE_TYPE_LABEL: Record<InvoiceTypeKey, string> = {
    received: 'Received', returned: 'Credit', return_slip: 'Return Slip', sublet: 'Sublet',
};

export const FLAG_TYPE_ORDER: FlagTypeKey[] = ['ro', 'match', 'duplicate', 'math', 'vendor', 'specialVendor', 'locationMismatch', 'stale', 'price', 'quantity', 'amount', 'error'];
export const FLAG_TYPE_LABEL: Record<FlagTypeKey, string> = {
    ro: 'RO', match: 'Match', duplicate: 'Duplicate', math: 'Math', vendor: 'Vendor',
    specialVendor: 'Special Vendor', locationMismatch: 'Shop', stale: 'Stale', price: 'Price', quantity: 'Quantity', amount: 'Amount', error: 'Error',
};
const SERIES_PREFIX = 's__';

function barClickRow(data: unknown): Record<string, unknown> | null {
    if (!data || typeof data !== 'object') return null;
    const record = data as { payload?: unknown };
    const source = record.payload && typeof record.payload === 'object' ? record.payload : data;
    return source as Record<string, unknown>;
}

const formatCompactCurrency = (v: number): string => {
    const abs = Math.abs(v); const sign = v < 0 ? '-' : '';
    if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
    if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(abs >= 10_000 ? 0 : 1)}K`;
    return `${sign}$${Math.round(abs).toLocaleString()}`;
};
const formatCompactNumber = (v: number): string => {
    const abs = Math.abs(v);
    if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
    if (abs >= 1000) return `${(v / 1000).toFixed(abs >= 10_000 ? 0 : 1)}K`;
    return Math.round(v).toLocaleString();
};
const fullCurrency = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
const fullNumber = (v: number) => Math.round(v).toLocaleString();
const formatPercent = (v: number) => `${(v || 0).toFixed(1)}%`;

type MetricFormat = 'currency' | 'percent' | 'number';

function unitToChartFormat(unit: Unit): MetricFormat {
    if (unit === 'value') return 'currency';
    if (unit === 'percent') return 'percent';
    return 'number';
}

function computeTimeseriesYExtents(
    chartData: Record<string, unknown>[],
    options: {
        stacked: boolean;
        series: Series[];
        hasComparisonBarData: boolean;
        dim: CategoryDim;
    },
): { dataMin: number; dataMax: number; minValue: number } {
    let dataMin = 0;
    let dataMax = 0;

    const currentKeys = options.series.map((s) => `${SERIES_PREFIX}${s.key}`);
    const priorKeys = options.series.map((s) => `prior_${s.label}`);

    const accumulate = (vals: number[]) => {
        if (options.stacked) {
            const positiveSum = vals.filter((v) => v > 0).reduce((a, b) => a + b, 0);
            const negativeSum = vals.filter((v) => v < 0).reduce((a, b) => a + b, 0);
            dataMax = Math.max(dataMax, positiveSum);
            dataMin = Math.min(dataMin, negativeSum);
        } else {
            vals.forEach((v) => {
                if (!Number.isNaN(v)) {
                    dataMax = Math.max(dataMax, v);
                    dataMin = Math.min(dataMin, v);
                }
            });
        }
    };

    chartData.forEach((row) => {
        accumulate(currentKeys.map((k) => Number(row[k]) || 0));
        if (options.hasComparisonBarData) {
            if (options.dim === 'none') {
                const prior = row.priorMetricValue;
                if (typeof prior === 'number' && !Number.isNaN(prior)) accumulate([prior]);
            } else {
                accumulate(priorKeys.flatMap((k) => {
                    const prior = row[k];
                    return typeof prior === 'number' && !Number.isNaN(prior) ? [prior] : [];
                }));
            }
        }
    });

    return { dataMin, dataMax, minValue: Math.min(dataMin, 0) };
}

function renderInvoicingBarValueLabel(props: any, format: MetricFormat, isComparison = false, fontSize = BAR_VALUE_FONT) {
    return renderVerticalBarValueLabel(props, {
        formatValue: (v) => formatChartValue(v, format),
        minWidth: isComparison ? VERTICAL_BAR_LABEL_MIN_WIDTH_COMPARISON : VERTICAL_BAR_LABEL_MIN_WIDTH_SINGLE,
        fontSize,
    });
}

function renderHistogramBarLabel(
    props: any,
    format: MetricFormat,
    isComparison = false,
    fontSize = BAR_VALUE_FONT,
    preserveSign = false,
) {
    const raw = props?.payload?.rawValue;
    const signed = typeof raw === 'number' ? raw : Number(props?.value);
    if (!Number.isFinite(signed) || signed === 0) return null;
    return renderVerticalBarValueLabel({ ...props, value: signed }, {
        formatValue: (v) => formatChartValue(preserveSign ? v : Math.abs(v), format),
        minWidth: isComparison ? VERTICAL_BAR_LABEL_MIN_WIDTH_COMPARISON : VERTICAL_BAR_LABEL_MIN_WIDTH_SINGLE,
        fontSize,
    });
}

function renderHistogramPriorBarLabel(
    props: any,
    format: MetricFormat,
    fontSize = BAR_VALUE_FONT,
    preserveSign = false,
) {
    const raw = props?.payload?.priorRawValue;
    const signed = typeof raw === 'number' ? raw : Number(props?.value);
    if (!Number.isFinite(signed) || signed === 0) return null;
    return renderVerticalBarValueLabel({ ...props, value: signed }, {
        formatValue: (v) => formatChartValue(preserveSign ? v : Math.abs(v), format),
        minWidth: VERTICAL_BAR_LABEL_MIN_WIDTH_COMPARISON,
        fontSize,
    });
}

function sortCategoryData<T extends { value: number; rawValue?: number; name: string }>(
    data: T[],
    sortOrder: WidgetSort,
    opts?: { byRawValue?: boolean },
): T[] {
    return [...data].sort((a, b) => {
        const av = opts?.byRawValue && typeof a.rawValue === 'number' ? a.rawValue : a.value;
        const bv = opts?.byRawValue && typeof b.rawValue === 'number' ? b.rawValue : b.value;
        const cmp = sortOrder === 'desc' ? bv - av : av - bv;
        return cmp || a.name.localeCompare(b.name);
    });
}

interface Series { key: string; label: string; color: string; }

function seriesLegendItems(series: Series[]) {
    return series.map((s) => ({ key: s.key, label: s.label, color: s.color }));
}

function unitForMetric(metric: string): Unit {
    if (metric === 'autoSubmissionRate') return 'percent';
    if (
        metric === 'invoiceValue'
        || metric === 'invoiceFlagsValue'
        || metric === 'avgCostVariance'
        || metric === 'totalCostVariance'
    ) return 'value';
    if (metric === 'avgDeliveryVarianceDays') return 'count';
    return 'count';
}

/** Cost/delivery variance can be negative; histogram must plot signed values, not abs. */
function isSignedHistogramMetric(metric: string): boolean {
    return metric === 'totalCostVariance'
        || metric === 'avgCostVariance'
        || metric === 'avgDeliveryVarianceDays';
}

function isBrokenDown(chartStyle: ChartStyle, domain: WidgetDomain, metric: string): boolean {
    if (domain === 'invoicingLocation' || domain === 'invoicingVendor') return true;
    if (chartStyle === 'stacked' || chartStyle === 'donut' || chartStyle === 'histogram') return true;
    // Time-series bars: cluster invoice types side-by-side per date (not a single total bar).
    if (chartStyle === 'bars' && domain === 'invoicingTotal' && (metric === 'invoiceCount' || metric === 'invoiceValue')) {
        return true;
    }
    return false;
}

function getCategoryDim(metric: string, domain: WidgetDomain, chartStyle: ChartStyle): CategoryDim {
    if (!isBrokenDown(chartStyle, domain, metric)) return 'none';
    if (domain === 'invoicingLocation') return 'location';
    if (domain === 'invoicingVendor') return 'vendor';
    if (metric === 'invoiceFlags' || metric === 'invoiceFlagsValue') return 'flagType';
    if (metric === 'autoSubmissionRate') return 'none';
    return 'invoiceType';
}

function locationSegValue(seg: InvoicingLocationSegment | undefined, metric: string): number {
    if (!seg) return 0;
    switch (metric) {
        case 'invoiceCount': return seg.count ?? 0;
        case 'invoiceValue': return seg.value ?? 0;
        case 'invoiceFlags': return seg.manuallySubmittedCount;
        case 'invoiceFlagsValue': return seg.flagValue ?? 0;
        case 'autoSubmissionRate': return seg.autoSubmissionRate;
        default: return seg.count ?? 0;
    }
}

/** Returns null when delivery variance has no samples (do not treat as 0). */
function vendorSegValue(seg: InvoicingVendorSegment | undefined, metric: string): number | null {
    if (!seg) return metric === 'avgDeliveryVarianceDays' ? null : 0;
    switch (metric) {
        case 'invoiceCount': return seg.count ?? 0;
        case 'invoiceValue': return seg.value ?? 0;
        case 'avgCostVariance': return seg.avgCostVariance ?? 0;
        case 'totalCostVariance': return seg.costVarianceSum ?? 0;
        case 'avgDeliveryVarianceDays':
            // Filler points can emit 0 with deliveryVariancePartCount === 0 — treat as null.
            if (seg.deliveryVariancePartCount === 0) return null;
            return seg.avgDeliveryVarianceDays === null || seg.avgDeliveryVarianceDays === undefined
                ? null
                : seg.avgDeliveryVarianceDays;
        case 'matchedPartCount': return seg.matchedPartCount ?? 0;
        default: return 0;
    }
}

function deriveVendors(
    points: InvoicingTimeseriesDataPoint[],
    priorPoints: Array<Pick<InvoicingTimeseriesDataPoint, 'byVendor'>> = [],
): Array<{ id: string; name: string }> {
    const totals = new Map<string, { name: string; value: number }>();
    const addPoint = (p: Pick<InvoicingTimeseriesDataPoint, 'byVendor'>) => {
        Object.entries(p.byVendor ?? {}).forEach(([id, seg]) => {
            const prev = totals.get(id);
            const add = Math.abs(seg.value ?? 0);
            if (prev) {
                prev.value += add;
            } else {
                totals.set(id, { name: seg.vendorName || id, value: add });
            }
        });
    };
    points.forEach(addPoint);
    priorPoints.forEach(addPoint);
    return Array.from(totals.entries())
        .sort((a, b) => b[1].value - a[1].value)
        .map(([id, v]) => ({ id, name: v.name }));
}

function bucketTotal(point: InvoicingTimeseriesDataPoint, metric: string): number {
    switch (metric) {
        case 'invoiceCount': return point.invoiceCount;
        case 'invoiceValue': return point.value;
        case 'invoiceFlags': return point.manuallySubmittedCount;
        case 'invoiceFlagsValue': return point.flagValue;
        case 'autoSubmissionRate': return point.autoSubmissionRate;
        case 'avgCostVariance':
        case 'totalCostVariance':
        case 'avgDeliveryVarianceDays':
        case 'matchedPartCount': {
            // Total-domain rollup from byVendor when present
            const segs = Object.values(point.byVendor ?? {});
            if (metric === 'matchedPartCount') {
                return segs.reduce((s, v) => s + (v.matchedPartCount ?? 0), 0);
            }
            if (metric === 'totalCostVariance') {
                return segs.reduce((s, v) => s + (v.costVarianceSum ?? 0), 0);
            }
            if (metric === 'avgCostVariance') {
                const matched = segs.reduce((s, v) => s + (v.matchedPartCount ?? 0), 0);
                if (matched === 0) return 0;
                const sum = segs.reduce((s, v) => s + (v.avgCostVariance ?? 0) * (v.matchedPartCount ?? 0), 0);
                return sum / matched;
            }
            // avgDeliveryVarianceDays — skip filler zeros with no samples
            let daysSum = 0;
            let partCount = 0;
            segs.forEach((v) => {
                if (v.deliveryVariancePartCount === 0) return;
                if (v.avgDeliveryVarianceDays == null) return;
                daysSum += v.avgDeliveryVarianceDays * v.deliveryVariancePartCount;
                partCount += v.deliveryVariancePartCount;
            });
            return partCount > 0 ? daysSum / partCount : 0;
        }
        default: return point.invoiceCount;
    }
}

function priorBucketTotal(
    point: InvoicingTimeseriesDataPoint['prior'] | undefined,
    metric: string,
): number | undefined {
    if (!point) return undefined;
    if (
        metric === 'avgCostVariance'
        || metric === 'totalCostVariance'
        || metric === 'avgDeliveryVarianceDays'
        || metric === 'matchedPartCount'
    ) {
        return bucketTotal(point as InvoicingTimeseriesDataPoint, metric);
    }
    let value: unknown;
    switch (metric) {
        case 'invoiceCount': value = point.invoiceCount; break;
        case 'invoiceValue': value = point.value; break;
        case 'invoiceFlags': value = point.manuallySubmittedCount; break;
        case 'invoiceFlagsValue': value = point.flagValue; break;
        case 'autoSubmissionRate': value = point.autoSubmissionRate; break;
        default: value = point.invoiceCount;
    }
    return typeof value === 'number' ? value : undefined;
}

/** Keep buckets with current activity, or prior-only buckets when comparison is shown. */
function filterVisibleTimeseriesPoints(
    points: InvoicingTimeseriesDataPoint[],
    includePriorOnlyBuckets: boolean,
    metric: string,
): InvoicingTimeseriesDataPoint[] {
    return filterTimeseriesPointsForDisplay(
        points,
        { includePriorOnlyBuckets },
        (point) => getInvoicingTimeseriesActivity(point, metric),
    );
}

function metricLabel(metric: string): string {
    switch (metric) {
        case 'invoiceCount': return 'Submitted Invoices';
        case 'invoiceValue': return 'Submitted Invoices';
        case 'invoiceFlags': return 'Invoice Flags';
        case 'invoiceFlagsValue': return 'Invoice Flags ($)';
        case 'autoSubmissionRate': return 'Auto Submission Rate';
        case 'avgCostVariance': return 'Avg. Cost Variance';
        case 'totalCostVariance': return 'Total Cost Variance';
        case 'avgDeliveryVarianceDays': return 'Avg. Delivery Variance';
        case 'matchedPartCount': return 'Matched Parts';
        default: return 'Submitted Invoices';
    }
}

/** Backend change keys use raw datapoint field names, not widget metric ids.
 *  Variance metrics are byVendor-derived and never appear on point.change. */
function backendChangeKey(metric: string): string | undefined {
    switch (metric) {
        case 'invoiceCount': return 'invoiceCount';
        case 'invoiceValue': return 'value';
        case 'invoiceFlags': return 'manuallySubmittedCount';
        case 'invoiceFlagsValue': return 'flagValue';
        case 'autoSubmissionRate': return 'autoSubmissionRate';
        default: return undefined;
    }
}

function resolveBackendChange(
    change: ComparisonChange | undefined,
    metric: string,
): number | undefined {
    const key = backendChangeKey(metric);
    if (!key) return undefined;
    const value = change?.[key];
    return typeof value === 'number' ? value : undefined;
}

/** Prefer backend change for top-level metrics; fall back to local compute for byVendor-derived metrics. */
function resolveMetricChange(
    change: ComparisonChange | undefined,
    metric: string,
    value: number,
    priorValue: number | undefined,
    unit: Unit,
): { change?: number; changeLabel: string } {
    const backend = resolveBackendChange(change, metric);
    if (backend !== undefined) {
        return { change: backend, changeLabel: unit === 'percent' ? 'pp' : '%' };
    }
    return computeChange(value, priorValue, unit);
}

function computeChange(value: number, priorValue: number | undefined, unit: Unit): { change?: number; changeLabel: string } {
    const change = calculateMetricChange(value, priorValue, unit === 'percent');
    return {
        change,
        changeLabel: unit === 'percent' ? 'pp' : '%',
    };
}

function isPriorTooltipEntry(entry: { name?: string; dataKey?: string; payload?: Record<string, unknown> }): boolean {
    const dataKey = String(entry.dataKey ?? entry.payload?.dataKey ?? '');
    const name = String(entry.name ?? '');
    return dataKey === 'priorMetricValue'
        || dataKey === 'priorValue'
        || dataKey.startsWith('prior_')
        || name.endsWith(' (prior)')
        || name.endsWith('(prior)');
}

function priorSeriesDisplayName(entry: { name?: string; dataKey?: string }): string {
    const name = String(entry.name ?? '');
    if (name.endsWith(' (prior)')) return name.slice(0, -' (prior)'.length);
    const dataKey = String(entry.dataKey ?? '');
    if (dataKey.startsWith('prior_')) return dataKey.slice('prior_'.length);
    return name;
}

function changeColor(isPositive: boolean, metric: string): string {
    const isNegativeMetric = NEGATIVE_METRICS.has(metric);
    if (isNegativeMetric) return isPositive ? 'red.500' : 'green.500';
    return isPositive ? 'green.500' : 'red.500';
}

export const DistributionTooltip = ({
    active,
    payload,
    metric,
    unit,
    fmt,
    comparison,
    preset,
    granularity,
}: {
    active?: boolean;
    payload?: Array<{ name?: string; dataKey?: string; color?: string; value?: number; payload?: { name?: string; color?: string; rawValue?: number; priorRawValue?: number } }>;
    metric: string;
    unit: Unit;
    fmt: (v: number) => string;
    comparison?: ReportingComparison;
    preset?: string;
    granularity?: string;
}) => {
    if (!active || !payload?.length) return null;

    const entry = payload[0];
    const item = entry.payload ?? {};
    const label = String(item.name ?? entry.name ?? '');
    const color = String(item.color ?? entry.color ?? PRIMARY_CHART_COLOR);
    const isPrior = isPriorTooltipEntry(entry);
    const currentValue = Number(item.rawValue ?? entry.value ?? 0);
    const priorValue = item.priorRawValue as number | undefined;
    const comparisonLabel = getTimeseriesComparisonLabel(preset, granularity) ?? 'vs prior';
    const { change, changeLabel } = computeChange(
        currentValue,
        comparison ? priorValue : undefined,
        unit,
    );
    const isPositive = change !== undefined && change >= 0;
    const displayLabel = isPrior ? `Prior ${label}` : label;
    const primaryValue = isPrior ? priorValue : currentValue;

    return (
        <Box bg="white" p={3} border="1px solid" borderColor="gray.200" borderRadius="lg" boxShadow="none" minW="150px">
            <Flex align="center" gap={2} mb={1}>
                <Box w={2} h={2} bg={color} borderRadius="sm" />
                <Text fontSize="sm" fontWeight="semibold" color="gray.700">{displayLabel}</Text>
            </Flex>
            <Text fontSize="xs" color="gray.500" mb={1}>{metricLabel(metric)}</Text>
            <Text fontSize="lg" fontWeight="bold" color="#0071E3" mb={1}>
                {primaryValue === undefined ? '—' : fmt(primaryValue)}
            </Text>
            {comparison && (isPrior ? currentValue !== undefined : priorValue !== undefined) && (
                <Text fontSize="xs" color="gray.500" mb={1}>
                    {isPrior ? `Current: ${fmt(currentValue)}` : `Prior: ${fmt(priorValue!)}`}
                </Text>
            )}
            {change !== undefined && (
                <Flex align="center" gap={1}>
                    <Icon as={(isPositive ? TbTrendingUp : TbTrendingDown) as React.ElementType} color={changeColor(isPositive, metric)} boxSize={3} />
                    <Text fontSize="xs" fontWeight="medium" color={changeColor(isPositive, metric)}>
                        {Math.abs(change).toFixed(1)}{changeLabel}
                        <Text as="span" color="gray.400" ml={1} fontWeight="normal">{comparisonLabel}</Text>
                    </Text>
                </Flex>
            )}
            {comparison && change === undefined && (
                <Text fontSize="xs" color="gray.400">—</Text>
            )}
        </Box>
    );
};

function seriesKeyFromTooltipDataKey(dataKey: string): string | null {
    if (!dataKey.startsWith(SERIES_PREFIX)) return null;
    return dataKey.slice(SERIES_PREFIX.length);
}

type TooltipPayloadEntry = {
    name?: string;
    dataKey?: string;
    value?: number;
    color?: string;
    cy?: number;
    payload?: Record<string, unknown>;
};

function pickNearestLinePayload(
    payload: TooltipPayloadEntry[],
    mouseY: number | null | undefined,
    viewBox?: { x?: number; y?: number; width?: number; height?: number },
    yDomain?: [number, number],
): TooltipPayloadEntry | undefined {
    const candidates = payload.filter((entry) => {
        const dataKey = String(entry.dataKey ?? '');
        return dataKey.startsWith(SERIES_PREFIX);
    });
    if (!candidates.length) return undefined;

    if (mouseY == null || viewBox?.height == null || !yDomain) {
        return candidates[0];
    }

    const [yMin, yMax] = yDomain;
    const range = yMax - yMin || 1;
    const plotTop = viewBox.y ?? 0;
    const plotHeight = viewBox.height;

    return candidates.reduce(
        (best, entry) => {
            const value = Number(entry.value ?? 0);
            const pointY = plotTop + plotHeight * (1 - (value - yMin) / range);
            const dist = Math.abs(pointY - mouseY);
            return dist < best.dist ? { entry, dist } : best;
        },
        { entry: candidates[0], dist: Infinity },
    ).entry;
}

export const TimeseriesTooltip = ({
    active,
    payload,
    label,
    metric,
    unit,
    fmt,
    totalFmt,
    comparison,
    preset,
    granularity,
    multiSeries,
    pickNearestLine = false,
    lineHoverY,
    viewBox,
    yDomain,
    onActiveLineKey,
    periodStartDate,
    periodEndDate,
}: {
    active?: boolean;
    payload?: TooltipPayloadEntry[];
    label?: string;
    metric: string;
    unit: Unit;
    fmt: (v: number) => string;
    totalFmt: (v: number) => string;
    comparison?: ReportingComparison;
    preset?: string;
    granularity?: string;
    multiSeries: boolean;
    pickNearestLine?: boolean;
    lineHoverY?: number | null;
    viewBox?: { x?: number; y?: number; width?: number; height?: number };
    yDomain?: [number, number];
    onActiveLineKey?: (key: string | null) => void;
    periodStartDate?: string;
    periodEndDate?: string;
}) => {
    useEffect(() => {
        if (!pickNearestLine || !onActiveLineKey) return;
        if (!active || !payload?.length) {
            onActiveLineKey(null);
            return;
        }
        const nearest = pickNearestLinePayload(payload, lineHoverY, viewBox, yDomain);
        const dataKey = String(nearest?.dataKey ?? '');
        onActiveLineKey(seriesKeyFromTooltipDataKey(dataKey));
    }, [active, payload, lineHoverY, viewBox, yDomain, pickNearestLine, onActiveLineKey]);

    if (!active || !payload?.length) return null;

    const row = payload[0].payload ?? {};
    const dateStr = String(row.date ?? '');
    const isPartial = isPartialReportingPeriod(dateStr, granularity ?? 'week', periodEndDate, periodStartDate);
    const partialLabel = getPartialPeriodLabel(granularity ?? 'week');
    const currentPeriodLabel = formatPeriodTooltipLabel(
        dateStr,
        granularity ?? 'week',
        periodEndDate,
        periodStartDate,
    );
    const comparisonLabel = getTimeseriesComparisonLabel(preset, granularity) ?? 'vs prior';
    const priorPeriodLabel = comparison
        ? resolveComparablePriorPeriodLabel(
            dateStr,
            granularity ?? 'week',
            comparison,
            row.priorDate as string | undefined,
            row.priorEndDate as string | undefined,
            preset,
            periodEndDate,
            periodStartDate,
        )
        : undefined;
    const priorEntry = payload.find(
        (entry) => isPriorTooltipEntry(entry) && typeof entry.value === 'number',
    );

    if (priorEntry) {
        const isPriorMulti = multiSeries && String(priorEntry.dataKey ?? '').startsWith('prior_');
        const priorValue = priorEntry.value as number;
        const seriesName = isPriorMulti ? priorSeriesDisplayName(priorEntry) : metricLabel(metric);
        const valueFmt = isPriorMulti ? fmt : totalFmt;
        const currentValue = isPriorMulti
            ? row[`current_${seriesName}`] as number | undefined
            : row[`${SERIES_PREFIX}total`] as number | undefined;
        // Top-level prior bars: prefer backend change; fall back to local for byVendor-derived metrics.
        const localOrBackend = !isPriorMulti
            ? resolveMetricChange(
                row.change as ComparisonChange | undefined,
                metric,
                currentValue ?? 0,
                comparison ? priorValue : undefined,
                unit,
            )
            : undefined;
        const localChange = isPriorMulti
            ? (() => {
                const precomputed = row[`change_${seriesName}`];
                if (typeof precomputed === 'number') {
                    return { change: precomputed, changeLabel: unit === 'percent' ? 'pp' : '%' };
                }
                return computeChange(currentValue ?? 0, comparison ? priorValue : undefined, unit);
            })()
            : { change: undefined as number | undefined, changeLabel: unit === 'percent' ? 'pp' : '%' };
        const change = isPriorMulti ? localChange.change : localOrBackend?.change;
        const changeLabel = isPriorMulti
            ? localChange.changeLabel
            : (localOrBackend?.changeLabel ?? (unit === 'percent' ? 'pp' : '%'));
        const isPositive = change !== undefined && change >= 0;

        return (
            <Box bg="white" p={3} border="1px solid" borderColor="gray.200" borderRadius="lg" boxShadow="none" minW="150px">
                <Text fontSize="xs" color="gray.500" mb={1}>
                    {currentPeriodLabel} {isPartial && <Text as="span" color="red.500" fontWeight="medium">({partialLabel})</Text>}
                </Text>
                {isPriorMulti ? (
                    <Flex align="center" gap={2} mb={1}>
                        <Box w={2} h={2} bg={PRIOR_COMPARISON_BAR_FILL} borderRadius="sm" />
                        <Text fontSize="sm" fontWeight="semibold" color="gray.700">Prior {seriesName}</Text>
                    </Flex>
                ) : (
                    <Text fontSize="sm" fontWeight="semibold" color="gray.700">Prior {seriesName}</Text>
                )}
                <Text fontSize="lg" fontWeight="bold" color="#0071E3" mb={1}>{valueFmt(priorValue)}</Text>
                {comparison && currentValue !== undefined && (
                    <>
                        {priorPeriodLabel && (
                            <Text fontSize="xs" color="gray.500">Prior period: {priorPeriodLabel}</Text>
                        )}
                        <Text fontSize="xs" color="gray.500" mb={1}>Current: {valueFmt(currentValue)}</Text>
                    </>
                )}
                {comparison && currentValue === undefined && (
                    <Text fontSize="xs" color="gray.500" mb={1}>Current period: {currentPeriodLabel}</Text>
                )}
                {change !== undefined && (
                    <Flex align="center" gap={1}>
                        <Icon as={(isPositive ? TbTrendingUp : TbTrendingDown) as React.ElementType} color={changeColor(isPositive, metric)} boxSize={3} />
                        <Text fontSize="xs" fontWeight="medium" color={changeColor(isPositive, metric)}>
                            {Math.abs(change).toFixed(1)}{changeLabel}
                            <Text as="span" color="gray.400" ml={1} fontWeight="normal">{comparisonLabel}</Text>
                        </Text>
                    </Flex>
                )}
                <ComparisonTooltipFooter
                    comparison={comparison}
                    change={change}
                    priorValue={priorValue}
                    priorPeriodLabel={priorPeriodLabel}
                />
            </Box>
        );
    }

    if (!multiSeries) {
        const value = Number(payload[0].value ?? 0);
        const priorValue = resolveComparablePriorMetricValue(
            typeof row.priorMetricValue === 'number'
                ? row.priorMetricValue
                : priorBucketTotal(row.prior as InvoicingTimeseriesDataPoint['prior'], metric),
            {
                comparison,
                date: dateStr,
                priorDate: row.priorDate as string | undefined,
                granularity: granularity ?? 'week',
                preset,
            },
        );
        const backendOrLocal = resolveMetricChange(
            row.change as ComparisonChange | undefined,
            metric,
            value,
            comparison ? priorValue : undefined,
            unit,
        );
        const change = comparison ? backendOrLocal.change : undefined;
        const changeLabel = backendOrLocal.changeLabel;
        const isPositive = change !== undefined && change >= 0;

        return (
            <Box bg="white" p={3} border="1px solid" borderColor="gray.200" borderRadius="lg" boxShadow="none" minW="150px">
                <Text fontSize="xs" color="gray.500" mb={1}>
                    {currentPeriodLabel} {isPartial && <Text as="span" color="red.500" fontWeight="medium">({partialLabel})</Text>}
                </Text>
                <Text fontSize="sm" fontWeight="semibold" color="gray.700">{metricLabel(metric)}</Text>
                <Text fontSize="lg" fontWeight="bold" color="#0071E3" mb={1}>{totalFmt(value)}</Text>
                {comparison && priorValue !== undefined && (
                    <>
                        {priorPeriodLabel && (
                            <Text fontSize="xs" color="gray.500">Prior period: {priorPeriodLabel}</Text>
                        )}
                        <Text fontSize="xs" color="gray.500" mb={1}>Prior: {totalFmt(priorValue)}</Text>
                    </>
                )}
                {change !== undefined && (
                    <Flex align="center" gap={1}>
                        <Icon as={(isPositive ? TbTrendingUp : TbTrendingDown) as React.ElementType} color={changeColor(isPositive, metric)} boxSize={3} />
                        <Text fontSize="xs" fontWeight="medium" color={changeColor(isPositive, metric)}>
                            {Math.abs(change).toFixed(1)}{changeLabel}
                            <Text as="span" color="gray.400" ml={1} fontWeight="normal">{comparisonLabel}</Text>
                        </Text>
                    </Flex>
                )}
                <ComparisonTooltipFooter
                    comparison={comparison}
                    change={change}
                    priorValue={priorValue}
                    priorPeriodLabel={priorPeriodLabel}
                />
            </Box>
        );
    }

    const hovered = pickNearestLine
        ? pickNearestLinePayload(payload, lineHoverY, viewBox, yDomain)
        : payload.filter((entry) => {
            const dataKey = String(entry.dataKey ?? entry.name ?? '');
            return !dataKey.startsWith('prior_');
        })[0];
    if (!hovered) return null;

    const name = String(hovered.name ?? '');
    const value = Number(hovered.value ?? 0);
    const priorFromData = row[`prior_${name}`] as number | undefined;
    const priorValue = resolveComparablePriorMetricValue(
        typeof priorFromData === 'number' ? priorFromData : undefined,
        {
            comparison,
            date: dateStr,
            priorDate: row.priorDate as string | undefined,
            granularity: granularity ?? 'week',
            preset,
        },
    );
    const precomputedChange = row[`change_${name}`];
    const localChange = computeChange(value, comparison ? priorValue : undefined, unit);
    const change = typeof precomputedChange === 'number' ? precomputedChange : localChange.change;
    const changeLabel = localChange.changeLabel;
    const isPositive = change !== undefined && change >= 0;

    return (
        <Box bg="white" p={3} border="1px solid" borderColor="gray.200" borderRadius="lg" boxShadow="none" minW="150px">
            <Text fontSize="xs" color="gray.500" mb={1}>
                {currentPeriodLabel} {isPartial && <Text as="span" color="red.500" fontWeight="medium">({partialLabel})</Text>}
            </Text>
            <Flex align="center" gap={2} mb={1}>
                <Box w={2} h={2} bg={hovered.color} borderRadius="sm" />
                <Text fontSize="sm" fontWeight="semibold" color="gray.700">{name}</Text>
            </Flex>
            <Text fontSize="lg" fontWeight="bold" color="#0071E3" mb={1}>{fmt(value)}</Text>
            {comparison && priorValue !== undefined && (
                <>
                    {priorPeriodLabel && (
                        <Text fontSize="xs" color="gray.500">Prior period: {priorPeriodLabel}</Text>
                    )}
                    <Text fontSize="xs" color="gray.500" mb={1}>Prior: {fmt(priorValue)}</Text>
                </>
            )}
            {change !== undefined && (
                <Flex align="center" gap={1}>
                    <Icon as={(isPositive ? TbTrendingUp : TbTrendingDown) as React.ElementType} color={changeColor(isPositive, metric)} boxSize={3} />
                    <Text fontSize="xs" fontWeight="medium" color={changeColor(isPositive, metric)}>
                        {Math.abs(change).toFixed(1)}{changeLabel}
                        <Text as="span" color="gray.400" ml={1} fontWeight="normal">{comparisonLabel}</Text>
                    </Text>
                </Flex>
            )}
            <ComparisonTooltipFooter
                comparison={comparison}
                change={change}
                priorValue={priorValue}
                priorPeriodLabel={priorPeriodLabel}
            />
        </Box>
    );
};

const InvoicingTimeseriesChart = ({
    data,
    isLoading,
    metric,
    domain,
    chartStyle,
    height = '100%',
    comparison,
    preset,
    granularity,
    periodStartDate,
    periodEndDate,
    showComparisonBars = false,
    initialSortOrder = 'desc',
    onSortOrderChange,
    locationCutoff = INVOICING_DEFAULT_LOCATION_LINE_CUTOFF,
    locationShowAll = false,
    invoiceTypes,
    onSegmentClick,
}: InvoicingTimeseriesChartProps) => {
    const [sortOrder, setSortOrder] = useState<WidgetSort>(initialSortOrder);
    const [activeLineKey, setActiveLineKey] = useState<string | null>(null);
    const [lineHoverY, setLineHoverY] = useState<number | null>(null);
    const [chartWidthRef, chartContainerWidth] = useChartContainerWidth();
    const isFullscreenAxis = useReportingChartFullscreen();
    const axisWidth = yAxisWidth(isFullscreenAxis);
    const lineLeftPad = lineChartPlotLeftPad(isFullscreenAxis);
    const isFlexHeight = typeof height === 'string';

    useEffect(() => {
        setSortOrder(initialSortOrder);
    }, [initialSortOrder]);

    useEffect(() => {
        setActiveLineKey(null);
        setLineHoverY(null);
    }, [metric, domain, chartStyle, invoiceTypes]);

    const points: InvoicingTimeseriesDataPoint[] = data?.data ?? [];
    // priorData omits nested comparison fields; structurally compatible for rollups.
    const priorPoints = (data?.priorData ?? []) as InvoicingTimeseriesDataPoint[];
    const locations = data?.locations ?? [];
    const vendors = deriveVendors(points, priorPoints);
    const unit = unitForMetric(metric);
    const effectiveChartStyle: ChartStyle =
        chartStyle === 'donut' && !invoicingMetricAllowsDonut(metric) ? 'line' : chartStyle;
    const dim = getCategoryDim(metric, domain, effectiveChartStyle);

    const segUnit: Unit = dim === 'location' || dim === 'vendor'
        ? unit
        : dim === 'flagType'
            ? (metric === 'invoiceFlagsValue' ? 'value' : 'count')
            : (unit === 'percent' ? 'count' : unit);
    const segFmtFull = segUnit === 'value' ? fullCurrency : segUnit === 'percent' ? formatPercent : fullNumber;
    const totalFmtFull = unit === 'percent' ? formatPercent : unit === 'value' ? fullCurrency : fullNumber;
    const fmtUnit = dim === 'none' ? unit : segUnit;
    const axisFmt = fmtUnit === 'percent' ? formatPercent : fmtUnit === 'value' ? formatCompactCurrency : formatCompactNumber;

    const invoiceTypeOrder = invoiceTypes && invoiceTypes.length > 0
        ? INVOICE_TYPE_ORDER.filter((t) => invoiceTypes.includes(t))
        : INVOICE_TYPE_ORDER;

    let series: Series[] = [];
    if (dim === 'invoiceType') {
        series = invoiceTypeOrder.map((t) => ({ key: `type:${t}`, label: INVOICE_TYPE_LABEL[t], color: INVOICE_TYPE_COLOR[t] }));
    } else if (dim === 'flagType') {
        series = FLAG_TYPE_ORDER.map((t, i) => ({ key: `flag:${t}`, label: FLAG_TYPE_LABEL[t], color: chartSeriesColor(i) }));
    } else if (dim === 'location') {
        series = locations.map((loc, i) => ({ key: `loc:${loc.id}`, label: loc.name, color: chartSeriesColor(i) }));
    } else if (dim === 'vendor') {
        series = vendors.map((v, i) => ({ key: `vend:${v.id}`, label: v.name, color: chartSeriesColor(i) }));
    } else {
        series = [{ key: 'total', label: metricLabel(metric), color: PRIMARY_CHART_COLOR }];
    }

    const segValue = (point: InvoicingTimeseriesDataPoint, key: string): number => {
        if (key === 'total') return bucketTotal(point, metric);
        if (key.startsWith('type:')) {
            const typeKey = key.slice(5) as InvoiceTypeKey;
            const seg = point.byInvoiceType?.[typeKey];
            if (!seg) return 0;
            if (segUnit === 'value') {
                const raw = seg.value;
                return typeKey === 'returned' ? Math.abs(raw) : raw;
            }
            return seg.count;
        }
        if (key.startsWith('flag:')) {
            const seg = point.byFlagType?.[key.slice(5) as FlagTypeKey];
            if (!seg) return 0;
            return segUnit === 'value' ? (seg.value ?? 0) : seg.count;
        }
        if (key.startsWith('loc:')) {
            return locationSegValue(point.byLocation?.[key.slice(4)], metric);
        }
        if (key.startsWith('vend:')) {
            const v = vendorSegValue(point.byVendor?.[key.slice(5)], metric);
            return v === null ? NaN : v;
        }
        return 0;
    };

    const priorSegValue = (
        point: InvoicingTimeseriesDataPoint['prior'] | undefined,
        key: string,
    ): number | undefined => {
        if (!point) return undefined;
        if (key === 'total') return priorBucketTotal(point, metric);
        if (key.startsWith('type:')) {
            const typeKey = key.slice(5) as InvoiceTypeKey;
            const seg = point.byInvoiceType?.[typeKey];
            if (!seg) return undefined;
            if (segUnit === 'value') {
                const raw = seg.value;
                return typeKey === 'returned' ? Math.abs(raw) : raw;
            }
            return seg.count;
        }
        if (key.startsWith('flag:')) {
            const seg = point.byFlagType?.[key.slice(5) as FlagTypeKey];
            if (!seg) return undefined;
            return segUnit === 'value' ? seg.value : seg.count;
        }
        if (key.startsWith('loc:')) {
            const seg = point.byLocation?.[key.slice(4)];
            if (!seg) return undefined;
            return locationSegValue(seg, metric);
        }
        if (key.startsWith('vend:')) {
            const v = vendorSegValue(point.byVendor?.[key.slice(5)], metric);
            return v === null ? undefined : v;
        }
        return undefined;
    };

    const hasSegment = (point: InvoicingTimeseriesDataPoint, key: string): boolean => {
        if (key === 'total') return true;
        if (key.startsWith('type:')) {
            return point.byInvoiceType?.[key.slice(5) as InvoiceTypeKey] !== undefined;
        }
        if (key.startsWith('flag:')) {
            return point.byFlagType?.[key.slice(5) as FlagTypeKey] !== undefined;
        }
        if (key.startsWith('loc:')) {
            return point.byLocation?.[key.slice(4)] !== undefined;
        }
        if (key.startsWith('vend:')) {
            const seg = point.byVendor?.[key.slice(5)];
            if (!seg) return false;
            if (metric === 'avgDeliveryVarianceDays') {
                return seg.deliveryVariancePartCount > 0
                    && seg.avgDeliveryVarianceDays !== null
                    && seg.avgDeliveryVarianceDays !== undefined;
            }
            return true;
        }
        return false;
    };

    const aggValue = (key: string, sourcePoints: InvoicingTimeseriesDataPoint[] = points): number => {
        if (metric === 'autoSubmissionRate') {
            let sub = 0;
            let den = 0;
            for (const p of sourcePoints) {
                if (key.startsWith('loc:')) {
                    const seg = p.byLocation?.[key.slice(4)];
                    sub += seg?.rateSubmitted ?? 0;
                    den += seg?.rateDenominator ?? 0;
                } else {
                    sub += p.rateSubmitted ?? 0;
                    den += p.rateDenominator ?? 0;
                }
            }
            return den > 0 ? Math.round((sub / den) * 1000) / 10 : 0;
        }

        // Part-weighted averages across date buckets (do not sum per-bucket averages).
        if (metric === 'avgCostVariance') {
            let weighted = 0;
            let matched = 0;
            for (const p of sourcePoints) {
                const segs = key.startsWith('vend:')
                    ? [p.byVendor?.[key.slice(5)]].filter(Boolean) as InvoicingVendorSegment[]
                    : Object.values(p.byVendor ?? {});
                for (const seg of segs) {
                    const parts = seg.matchedPartCount ?? 0;
                    if (parts <= 0) continue;
                    weighted += (seg.avgCostVariance ?? 0) * parts;
                    matched += parts;
                }
            }
            return matched > 0 ? weighted / matched : 0;
        }

        if (metric === 'avgDeliveryVarianceDays') {
            let daysSum = 0;
            let partCount = 0;
            for (const p of sourcePoints) {
                const segs = key.startsWith('vend:')
                    ? [p.byVendor?.[key.slice(5)]].filter(Boolean) as InvoicingVendorSegment[]
                    : Object.values(p.byVendor ?? {});
                for (const seg of segs) {
                    if (seg.deliveryVariancePartCount === 0) continue;
                    daysSum += seg.deliveryVarianceDaysSum ?? 0;
                    partCount += seg.deliveryVariancePartCount;
                }
            }
            return partCount > 0 ? daysSum / partCount : 0;
        }

        return sourcePoints.reduce((sum, p) => {
            const v = segValue(p, key);
            return sum + (Number.isFinite(v) ? v : 0);
        }, 0);
    };

    if ((dim === 'location' || dim === 'vendor') && effectiveChartStyle === 'line') {
        if (!locationShowAll && series.length > locationCutoff) {
            series = [...series]
                .sort((a, b) => aggValue(b.key) - aggValue(a.key))
                .slice(0, locationCutoff);
        } else {
            series = [...series].sort((a, b) => {
                const cmp = aggValue(b.key) - aggValue(a.key);
                return sortOrder === 'desc' ? cmp : -cmp || a.label.localeCompare(b.label);
            });
        }
        series = series.map((s, i) => ({ ...s, color: chartSeriesColor(i) }));
    }

    // Stack largest invoice-type segments at the bottom; colors follow size rank (dark → light).
    if (dim === 'invoiceType' && effectiveChartStyle === 'stacked' && segUnit !== 'percent') {
        series = [...series]
            .sort((a, b) => {
                const cmp = aggValue(b.key) - aggValue(a.key);
                return cmp || a.label.localeCompare(b.label);
            })
            .map((s, i) => ({ ...s, color: chartSeriesColor(i) }));
    }

    const isEmpty = points.length === 0;
    const containerSx = { h: '100%', display: 'flex', flexDirection: 'column' as const };
    const showLocationSort = (dim === 'location' || dim === 'vendor') && effectiveChartStyle === 'histogram';

    const chartHeight = height;
    const locationSortToggle = showLocationSort ? (
        <Box position="absolute" top={0} right={0} zIndex={2}>
            <IconButton
                aria-label="Sort order"
                icon={sortOrder === 'desc'
                    ? <Icon as={TbArrowDown as React.ElementType} />
                    : <Icon as={TbArrowUp as React.ElementType} />}
                size="sm"
                h="28px"
                variant="outline"
                borderColor="gray.300"
                _hover={{ borderColor: 'gray.400' }}
                bg="white"
                onClick={() => {
                    const next = sortOrder === 'desc' ? 'asc' : 'desc';
                    setSortOrder(next);
                    onSortOrderChange?.(next);
                }}
            />
        </Box>
    ) : null;

    if (isLoading && isEmpty) {
        return <Box {...containerSx}><Skeleton flex={1} borderRadius="lg" /></Box>;
    }
    if (isEmpty) {
        return <Box {...containerSx} alignItems="center" justifyContent="center"><Text color="gray.500">No data available</Text></Box>;
    }

    if (effectiveChartStyle === 'donut' || effectiveChartStyle === 'histogram') {
        const signedHistogram = effectiveChartStyle === 'histogram' && isSignedHistogramMetric(metric);
        const activeSeries = dim === 'none'
            ? [{ key: 'total', label: metricLabel(metric), color: PRIMARY_CHART_COLOR }]
            : series;
        let catData = activeSeries.map((s) => {
            const raw = aggValue(s.key);
            const priorSeriesPoints = priorPoints.filter((point) => hasSegment(point, s.key));
            const priorRaw = comparison && priorSeriesPoints.length
                ? aggValue(s.key, priorSeriesPoints)
                : undefined;
            const fullName = s.label;
            return {
                name: fullName,
                seriesKey: s.key,
                clickable: s.key !== 'flag:error',
                displayName: truncateRankingAxisName(fullName, isFullscreenAxis),
                color: s.color,
                rawValue: raw,
                priorRawValue: priorRaw,
                value: signedHistogram ? raw : Math.abs(raw),
                priorValue: priorRaw !== undefined
                    ? (signedHistogram ? priorRaw : Math.abs(priorRaw))
                    : undefined,
            };
        })
            .filter((d) => d.value !== 0 || (comparison && d.priorRawValue !== undefined && d.priorRawValue !== 0));

        catData = sortCategoryData(
            catData,
            effectiveChartStyle === 'donut' ? 'desc' : sortOrder,
            { byRawValue: signedHistogram },
        );

        if (effectiveChartStyle === 'donut') {
            catData = foldDonutOther(catData, (tail, otherValue) => {
                const rawValue = tail.reduce((sum, item) => sum + item.rawValue, 0);
                const priorRawValue = tail.some((item) => item.priorRawValue !== undefined)
                    ? tail.reduce((sum, item) => sum + (item.priorRawValue ?? 0), 0)
                    : undefined;
                return {
                    name: 'Other',
                    displayName: 'Other',
                    seriesKey: '',
                    clickable: false,
                    color: '',
                    rawValue,
                    priorRawValue,
                    value: otherValue,
                    priorValue: priorRawValue !== undefined ? Math.abs(priorRawValue) : undefined,
                };
            });
        }

        const rankColors = effectiveChartStyle === 'donut' ? PIE_DONUT_COLORS : BAR_SERIES_COLORS;
        catData = catData.map((d, i) => ({
            ...d,
            color: effectiveChartStyle === 'histogram'
                ? PRIMARY_CHART_COLOR
                : rankColors[i % rankColors.length],
        }));

        const hasHistogramComparison = effectiveChartStyle === 'histogram'
            && showComparisonBars
            && !!comparison
            && catData.some((d) => d.priorRawValue !== undefined && d.priorRawValue !== 0);

        const distributionTooltipProps = { metric, unit: segUnit, fmt: segFmtFull, comparison, preset, granularity };
        const chartLabelFormat = unitToChartFormat(segUnit);

        if (effectiveChartStyle === 'donut') {
            return (
                <Box {...containerSx} position="relative">
                    {locationSortToggle}
                    <ReportingDonut
                        slices={catData}
                        isFullscreen={isFullscreenAxis}
                        label={renderDonutValueLabel(chartLabelFormat, {
                            fontSize: chartValueFontSize(isFullscreenAxis),
                            isFullscreen: isFullscreenAxis,
                            asShare: true,
                        })}
                        tooltip={(
                            <Tooltip
                                content={(props: any) => <DistributionTooltip {...props} {...distributionTooltipProps} />}
                            />
                        )}
                        onSliceClick={onSegmentClick
                            ? (slice) => {
                                const seriesKey = (slice as { seriesKey?: string }).seriesKey;
                                if (!seriesKey) return;
                                onSegmentClick({ seriesKey, seriesLabel: slice.name });
                            }
                            : undefined}
                    />
                </Box>
            );
        }

        const isLocationHistogram = dim === 'location' || dim === 'vendor';
        const rankingAxis = getRankingXAxisLayout(
            catData.map((d) => d.displayName || ''),
            isFullscreenAxis,
        );
        const axisBand = rankingAxis.height;
        const axisBottom = rankingAxis.bottom;
        const axisFontSize = rankingAxis.fontSize;
        const histogramValues = catData.flatMap((d) => [
            d.value,
            ...(hasHistogramComparison && d.priorValue !== undefined ? [d.priorValue] : []),
        ]).filter((v): v is number => typeof v === 'number' && !Number.isNaN(v));
        const histDataMin = histogramValues.length ? Math.min(...histogramValues) : 0;
        const histDataMax = histogramValues.length ? Math.max(...histogramValues) : 0;
        const plotHeightPx = typeof height === 'string'
            ? Math.max(300, (typeof window !== 'undefined' ? window.innerHeight : 800) - 266)
            : Math.max(0, (typeof height === 'number' ? height : 280) - MARKETPLACE_CHART_TOP_MARGIN - axisBottom - axisBand);
        const [histYMin, histYMax] = getBarChartYDomain(histDataMin, histDataMax, {
            format: chartLabelFormat,
            capPercentMaxAt100: metric === 'autoSubmissionRate',
        }, plotHeightPx);
        const minBarWidth = hasHistogramComparison ? 68 : 40;
        const histogramChartWidth = isLocationHistogram
            ? Math.max(catData.length * minBarWidth, 100)
            : undefined;
        const showScrollbarGutter = isLocationHistogram
            && histogramChartWidth != null
            && needsRankingScrollbarGutter(histogramChartWidth, chartContainerWidth);
        const histogramValueFont = chartValueFontSize(isFullscreenAxis, !showScrollbarGutter);
        const histogramYAxisFont = yAxisTickFontSize(isFullscreenAxis, 10);

        const histogramChart = (
            <BarChart
                data={catData}
                margin={{
                    top: MARKETPLACE_CHART_TOP_MARGIN,
                    right: 25,
                    left: 0,
                    bottom: axisBottom,
                }}
                barGap={hasHistogramComparison ? 4 : undefined}
                barCategoryGap={hasHistogramComparison ? '20%' : '30%'}
            >
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis
                    dataKey="displayName"
                    tick={{ fontSize: axisFontSize, fill: '#4A5568' }}
                    tickLine={false}
                    axisLine={{ stroke: '#E2E8F0' }}
                    interval={0}
                    angle={-45}
                    textAnchor="end"
                    height={axisBand}
                    tickMargin={RANKING_AXIS_TICK_MARGIN}
                />
                <YAxis
                    domain={[histYMin, histYMax]}
                    ticks={getBarChartYTicks(histYMin, histYMax)}
                    tick={{ fontSize: histogramYAxisFont, fill: '#718096' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => (v === 0 && histYMin >= 0 ? '' : axisFmt(v))}
                    width={axisWidth}
                    interval={0}
                    allowDecimals={segUnit === 'percent'}
                />
                <Tooltip
                    content={(props: any) => <DistributionTooltip {...props} {...distributionTooltipProps} />}
                    cursor={{ fill: 'rgba(0, 113, 227, 0.05)' }}
                />
                {hasHistogramComparison && (
                    <Bar dataKey="priorValue" fill={PRIOR_COMPARISON_BAR_FILL} fillOpacity={PRIOR_COMPARISON_BAR_FILL_OPACITY} radius={[4, 4, 0, 0]}>
                        <LabelList content={(props: any) => renderHistogramPriorBarLabel(props, chartLabelFormat, histogramValueFont, signedHistogram)} />
                    </Bar>
                )}
                <Bar
                    dataKey="value"
                    radius={[4, 4, 0, 0]}
                    cursor={onSegmentClick ? 'pointer' : undefined}
                    onClick={onSegmentClick
                        ? (data) => {
                            const row = barClickRow(data);
                            const seriesKey = typeof row?.seriesKey === 'string' ? row.seriesKey : '';
                            if (!seriesKey || row?.clickable === false) return;
                            onSegmentClick({
                                seriesKey,
                                seriesLabel: typeof row?.name === 'string' ? row.name : undefined,
                            });
                        }
                        : undefined}
                >
                    {catData.map((d) => <Cell key={d.name} fill={d.color} />)}
                    <LabelList
                        content={(props: any) => (
                            hasHistogramComparison
                                ? renderInvoicingBarValueLabel(props, chartLabelFormat, true, histogramValueFont)
                                : renderHistogramBarLabel(props, chartLabelFormat, false, histogramValueFont, signedHistogram)
                        )}
                    />
                </Bar>
            </BarChart>
        );

        return (
            <Box
                position="relative"
                w="100%"
                minW={0}
                h={isFlexHeight ? '100%' : `${typeof height === 'number' ? height : 280}px`}
                minH={isFlexHeight ? 0 : undefined}
            >
                {locationSortToggle}
                {/* Fill the card; name band is letter-sized so bars keep the rest. */}
                <Box
                    ref={chartWidthRef}
                    overflowX={isLocationHistogram ? 'auto' : undefined}
                    overflowY="hidden"
                    w="100%"
                    height="100%"
                    pb={showScrollbarGutter ? `${getRankingScrollbarGutterPx()}px` : 0}
                >
                    <Box
                        minW={histogramChartWidth ? `${histogramChartWidth}px` : undefined}
                        w="100%"
                        h="100%"
                    >
                        <ResponsiveContainer width="100%" height="100%">
                            {histogramChart}
                        </ResponsiveContainer>
                    </Box>
                </Box>
            </Box>
        );
    }

    const visiblePoints = filterVisibleTimeseriesPoints(
        points,
        showComparisonBars && !!comparison,
        metric,
    );

    if (visiblePoints.length === 0) {
        return <Box {...containerSx} alignItems="center" justifyContent="center"><Text color="gray.500">No data available</Text></Box>;
    }

    const chartData = visiblePoints.map((point) => {
        const row: Record<string, number | string | InvoicingTimeseriesDataPoint['prior'] | ComparisonChange | undefined> = {
            date: point.date,
            displayDate: formatPeriodAxisLabel(point.date, granularity ?? 'week', periodEndDate, periodStartDate),
            priorDate: point.priorDate ?? '',
            priorEndDate: point.priorEndDate,
            prior: point.prior,
            change: point.change,
        };
        series.forEach((s) => {
            const current = segValue(point, s.key);
            row[`${SERIES_PREFIX}${s.key}`] = current;
            row[`current_${s.label}`] = current;
            const prior = priorSegValue(point.prior, s.key);
            row[`prior_${s.label}`] = resolveComparablePriorMetricValue(prior, {
                comparison,
                date: point.date,
                priorDate: point.priorDate,
                granularity: granularity ?? 'week',
                preset,
            });
            if (typeof row[`prior_${s.label}`] === 'number') {
                const nestedChange = calculateMetricChange(current, row[`prior_${s.label}`] as number, segUnit === 'percent');
                if (nestedChange !== undefined) {
                    row[`change_${s.label}`] = nestedChange;
                }
            }
        });

        if (dim === 'none') {
            row.priorMetricValue = resolveComparablePriorMetricValue(
                priorBucketTotal(point.prior, metric),
                {
                    comparison,
                    date: point.date,
                    priorDate: point.priorDate,
                    granularity: granularity ?? 'week',
                    preset,
                },
            );
        }

        return row;
    });

    const stacked = effectiveChartStyle === 'stacked' && segUnit !== 'percent';
    const showLegend = series.length > 1;
    const multiSeries = series.length > 1;
    const chartLabelFormat = unitToChartFormat(fmtUnit);

    const hasComparisonBarData = showComparisonBars && !!comparison && (
        dim === 'none'
            ? chartData.some((row) => typeof row.priorMetricValue === 'number' && !Number.isNaN(row.priorMetricValue as number))
            : chartData.some((row) => series.some((s) => {
                const priorVal = row[`prior_${s.label}`];
                return typeof priorVal === 'number' && !Number.isNaN(priorVal);
            }))
    );

    const { dataMin, dataMax, minValue } = computeTimeseriesYExtents(chartData, {
        stacked,
        series,
        hasComparisonBarData,
        dim,
    });
    const isPercentAxis = fmtUnit === 'percent';
    const effectiveGranularity = granularity ?? 'week';
    const axisLabels = chartData.map((row) => String(row.displayDate ?? ''));
    const maxAxisLabelLen = axisLabels.reduce((max, label) => Math.max(max, label.length), 0);
    const plotWidthPx = chartContainerWidth > 0
        ? Math.max(
            0,
            getTimeseriesPlotWidth(chartContainerWidth, axisWidth)
                - (effectiveChartStyle === 'line' ? lineLeftPad : 0),
        )
        : undefined;
    const xAxisLayout = getTimeseriesXAxisLayout(
        effectiveGranularity,
        chartData.length,
        maxAxisLabelLen,
        isFullscreenAxis,
        plotWidthPx,
        axisLabels,
    );
    const timeseriesBarTopMargin = MARKETPLACE_CHART_TOP_MARGIN;
    const plotHeightPx = typeof height === 'string'
        ? Math.max(300, (typeof window !== 'undefined' ? window.innerHeight : 800) - 266)
        : Math.max(0, (typeof height === 'number' ? height : 280) - timeseriesBarTopMargin - xAxisLayout.bottom - xAxisLayout.height);
    const [yMin, yMax] = getBarChartYDomain(dataMin, dataMax, {
        format: chartLabelFormat,
        capPercentMaxAt100: metric === 'autoSubmissionRate',
    }, plotHeightPx);

    const timeseriesValueFont = chartValueFontSize(isFullscreenAxis);
    const timeseriesYAxisFont = yAxisTickFontSize(isFullscreenAxis, 11);

    const tooltipProps = {
        metric,
        unit: fmtUnit,
        fmt: segFmtFull,
        totalFmt: totalFmtFull,
        comparison,
        preset,
        granularity,
        multiSeries,
        periodStartDate,
        periodEndDate,
    };

    return (
        <Box {...containerSx} position="relative">
            {locationSortToggle}
            <Box
                ref={chartWidthRef}
                position="relative"
                flex={1}
                minH={0}
                minW={0}
                w="100%"
                onMouseMove={(e) => {
                    if (effectiveChartStyle !== 'line' || !multiSeries) return;
                    const svg = e.currentTarget.querySelector('svg');
                    if (!svg) return;
                    const svgRect = svg.getBoundingClientRect();
                    setLineHoverY(e.clientY - svgRect.top);
                }}
                onMouseLeave={effectiveChartStyle === 'line' ? () => {
                    setActiveLineKey(null);
                    setLineHoverY(null);
                } : undefined}
            >
                {showLegend && <MarketplaceChartLegend items={seriesLegendItems(series)} isFullscreen={isFullscreenAxis} />}
                <ResponsiveContainer width="100%" height={chartHeight}>
                    {effectiveChartStyle === 'line' ? (
                        <LineChart data={chartData} margin={{ top: MARKETPLACE_CHART_TOP_MARGIN, right: 10, left: 0, bottom: xAxisLayout.bottom }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                            <XAxis
                                dataKey="displayDate"
                                tickLine={false}
                                axisLine={{ stroke: '#E2E8F0' }}
                                angle={xAxisLayout.angle}
                                textAnchor={xAxisLayout.textAnchor}
                                height={xAxisLayout.height}
                                tickMargin={xAxisLayout.tickMargin}
                                padding={{ left: lineLeftPad, right: xAxisLayout.labelPadX }}
                                interval={0}
                                tick={timeseriesXAxisTickRenderer(xAxisLayout, chartData.length, chartContainerWidth)}
                            />
                            <YAxis
                                domain={[yMin, yMax]}
                                ticks={getBarChartYTicks(yMin, yMax)}
                                tick={{ fontSize: timeseriesYAxisFont, fill: '#718096' }}
                                tickLine={false}
                                axisLine={false}
                                tickFormatter={(v) => (v === 0 && yMin >= 0 ? '' : axisFmt(v))}
                                width={axisWidth}
                                interval={0}
                                allowDecimals={isPercentAxis}
                            />
                            {minValue < 0 && <ReferenceLine y={0} stroke="#CBD5E0" strokeDasharray="3 3" />}
                            <Tooltip
                                shared={multiSeries}
                                content={(props: any) => (
                                    <TimeseriesTooltip
                                        {...props}
                                        {...tooltipProps}
                                        pickNearestLine={multiSeries}
                                        lineHoverY={lineHoverY}
                                        yDomain={[yMin, yMax]}
                                        onActiveLineKey={multiSeries ? setActiveLineKey : undefined}
                                    />
                                )}
                                cursor={{ stroke: '#CBD5E0' }}
                            />
                            {series.map((s) => (
                                <Line
                                    key={s.key}
                                    type="monotone"
                                    dataKey={`${SERIES_PREFIX}${s.key}`}
                                    name={s.label}
                                    stroke={s.color}
                                    strokeWidth={activeLineKey === s.key ? 3 : 2}
                                    strokeOpacity={1}
                                    opacity={1}
                                    dot={{ r: 2.5, strokeWidth: 0, fill: s.color }}
                                    activeDot={
                                        !multiSeries || activeLineKey === s.key
                                            ? { r: 5, strokeWidth: 2, fill: 'white', stroke: s.color }
                                            : false
                                    }
                                >
                                    {!multiSeries && (
                                        <LabelList
                                            dataKey={`${SERIES_PREFIX}${s.key}`}
                                            content={linePointLabelListContent(
                                                (v) => formatChartValue(v, chartLabelFormat),
                                                timeseriesValueFont,
                                                {
                                                    pointCount: chartData.length,
                                                    tickStep: xAxisLayout.tickStep,
                                                    plotLeft: axisWidth,
                                                    dateTick: {
                                                        labels: axisLabels,
                                                        fontSize: xAxisLayout.fontSize,
                                                        angle: xAxisLayout.angle,
                                                        textAnchor: xAxisLayout.textAnchor,
                                                        svgWidth: chartContainerWidth,
                                                    },
                                                },
                                            )}
                                        />
                                    )}
                                </Line>
                            ))}
                        </LineChart>
                    ) : (
                        <BarChart
                            data={chartData}
                            margin={{ top: MARKETPLACE_CHART_TOP_MARGIN, right: 10, left: 0, bottom: xAxisLayout.bottom }}
                            barGap={hasComparisonBarData ? 4 : undefined}
                            barCategoryGap="20%"
                        >
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                            <XAxis
                                dataKey="displayDate"
                                tickLine={false}
                                axisLine={{ stroke: '#E2E8F0' }}
                                angle={xAxisLayout.angle}
                                textAnchor={xAxisLayout.textAnchor}
                                height={xAxisLayout.height}
                                tickMargin={xAxisLayout.tickMargin}
                                padding={{ left: 0, right: xAxisLayout.labelPadX }}
                                interval={0}
                                tick={timeseriesXAxisTickRenderer(xAxisLayout, chartData.length, chartContainerWidth)}
                            />
                            <YAxis
                                domain={[yMin, yMax]}
                                ticks={getBarChartYTicks(yMin, yMax)}
                                tick={{ fontSize: timeseriesYAxisFont, fill: '#718096' }}
                                tickLine={false}
                                axisLine={false}
                                tickFormatter={(v) => (v === 0 && yMin >= 0 ? '' : axisFmt(v))}
                                width={axisWidth}
                                interval={0}
                                allowDecimals={isPercentAxis}
                            />
                            {minValue < 0 && <ReferenceLine y={0} stroke="#CBD5E0" strokeDasharray="3 3" />}
                            <Tooltip
                                shared={false}
                                content={(props: any) => <TimeseriesTooltip {...props} {...tooltipProps} />}
                                cursor={{ fill: 'rgba(0, 113, 227, 0.05)' }}
                            />
                            {hasComparisonBarData && dim === 'none' && (
                                <Bar dataKey="priorMetricValue" fill={PRIOR_COMPARISON_BAR_FILL} fillOpacity={PRIOR_COMPARISON_BAR_FILL_OPACITY} radius={[4, 4, 0, 0]}>
                                    <LabelList content={(props: any) => renderInvoicingBarValueLabel(props, chartLabelFormat, true, timeseriesValueFont)} />
                                </Bar>
                            )}
                            {hasComparisonBarData && dim !== 'none' && series.map((s, i) => {
                                const isLast = i === series.length - 1;
                                return (
                                    <Bar
                                        key={`prior-${s.key}`}
                                        dataKey={`prior_${s.label}`}
                                        name={`${s.label} (prior)`}
                                        legendType="none"
                                        fill={PRIOR_COMPARISON_BAR_FILL}
                                        fillOpacity={PRIOR_COMPARISON_BAR_FILL_OPACITY}
                                        stackId={stacked ? 'prior_stack' : undefined}
                                        radius={stacked ? (isLast ? [4, 4, 0, 0] : [0, 0, 0, 0]) : [4, 4, 0, 0]}
                                    >
                                        <LabelList
                                            dataKey={`prior_${s.label}`}
                                            {...(stacked
                                                ? { content: stackedSegmentLabelListContent((v) => formatChartValue(v, chartLabelFormat), timeseriesValueFont) }
                                                : { content: (props: any) => renderInvoicingBarValueLabel(props, chartLabelFormat, true, timeseriesValueFont) })}
                                        />
                                    </Bar>
                                );
                            })}
                            {series.map((s, i) => {
                                const isLast = i === series.length - 1;
                                return (
                                    <Bar
                                        key={s.key}
                                        dataKey={`${SERIES_PREFIX}${s.key}`}
                                        name={s.label}
                                        fill={s.color}
                                        stackId={stacked ? 'stack' : undefined}
                                        radius={stacked ? (isLast ? [4, 4, 0, 0] : [0, 0, 0, 0]) : [4, 4, 0, 0]}
                                        cursor={onSegmentClick && s.key !== 'flag:error' ? 'pointer' : undefined}
                                        onClick={onSegmentClick && s.key !== 'flag:error'
                                            ? (data) => {
                                                const row = barClickRow(data);
                                                const bucketStart = typeof row?.date === 'string' ? row.date : '';
                                                if (!bucketStart) return;
                                                onSegmentClick({
                                                    seriesKey: s.key,
                                                    seriesLabel: s.label,
                                                    bucketStart,
                                                });
                                            }
                                            : undefined}
                                    >
                                        <LabelList
                                            dataKey={`${SERIES_PREFIX}${s.key}`}
                                            {...(stacked
                                                ? { content: stackedSegmentLabelListContent((v) => formatChartValue(v, chartLabelFormat), timeseriesValueFont) }
                                                : { content: (props: any) => renderInvoicingBarValueLabel(props, chartLabelFormat, hasComparisonBarData, timeseriesValueFont) })}
                                        />
                                    </Bar>
                                );
                            })}
                        </BarChart>
                    )}
                </ResponsiveContainer>
            </Box>
        </Box>
    );
};

export default InvoicingTimeseriesChart;
